import {
  ethnicityOptions,
  familyOptions,
  genderPreferences,
  genders,
  hobbyOptions,
  languageOptions,
  maxHeight,
  minHeight,
  politicalOptions,
  professions,
  religionOptions,
  specialties,
  trainingLevels,
} from "../constants/data"

export const unique = (arr) => {
  return [...new Map(arr.map(v => [v?.id, v])).values()]
};

//helper method to return a user-friendly error message when things go wrong
export const getDisplayErrorMessage = (error_code) => {
  const authErrors = {
    "auth/argument-error": "",
    "auth/admin-restricted-operation":
      "This operation is restricted to administrators only.",
    "auth/app-not-authorized":
      "This app, identified by the domain where it's hosted, is not authorized to use Firebase Authentication with the provided API key. Review your key configuration in the Google API console.",
    "auth/app-not-installed":
      "The requested mobile application corresponding to the identifier (Android package name or iOS bundle ID) provided is not installed on this device.",
    "auth/captcha-check-failed":
      "The reCAPTCHA response token provided is either invalid, expired, already used or the domain associated with it does not match the list of whitelisted domains.",
    "auth/code-expired":
      "The SMS code has expired. Please re-send the verification code to try again.",
    "auth/cordova-not-ready": "Cordova framework is not ready.",
    "auth/cors-unsupported": "This browser is not supported.",
    "auth/credential-already-in-use":
      "This credential is already associated with a different user account.",
    "auth/custom-token-mismatch":
      "The custom token corresponds to a different audience.",
    "auth/requires-recent-login":
      "This operation is sensitive and requires recent authentication. Log in again before retrying this request.",
    "auth/dynamic-link-not-activated":
      "Please activate Dynamic Links in the Firebase Console and agree to the terms and conditions.",
    "auth/email-change-needs-verification":
      "Multi-factor users must always have a verified email.",
    "auth/email-already-in-use":
      "The email address is already in use by another account.",
    "auth/expired-action-code": "The action code has expired. ",
    "auth/cancelled-popup-request":
      "This operation has been cancelled due to another conflicting popup being opened.",
    "auth/internal-error": "An internal error has occurred.",
    "auth/invalid-app-credential":
      "The phone verification request contains an invalid application verifier. The reCAPTCHA token response is either invalid or expired.",
    "auth/invalid-app-id":
      "The mobile app identifier is not registed for the current project.",
    "auth/invalid-user-token":
      "This user's credential isn't valid for this project. This can happen if the user's token has been tampered with, or if the user isn't for the project associated with this API key.",
    "auth/invalid-auth-event": "An internal error has occurred.",
    "auth/invalid-verification-code":
      "The SMS verification code used to create the phone auth credential is invalid. Please resend the verification code sms and be sure use the verification code provided by the user.",
    "auth/invalid-continue-uri":
      "The continue URL provided in the request is invalid.",
    "auth/invalid-cordova-configuration":
      "The following Cordova plugins must be installed to enable OAuth sign-in: cordova-plugin-buildinfo, cordova-universal-links-plugin, cordova-plugin-browsertab, cordova-plugin-inappbrowser and cordova-plugin-customurlscheme.",
    "auth/invalid-custom-token":
      "The custom token format is incorrect. Please check the documentation.",
    "auth/invalid-dynamic-link-domain":
      "The provided dynamic link domain is not configured or authorized for the current project.",
    "auth/invalid-email": "The email address is badly formatted.",
    "auth/invalid-api-key":
      "Your API key is invalid, please check you have copied it correctly.",
    "auth/invalid-cert-hash": "The SHA-1 certificate hash provided is invalid.",
    "auth/invalid-credential":
      "The supplied auth credential is malformed or has expired.",
    "auth/invalid-message-payload":
      "The email template corresponding to this action contains invalid characters in its message. Please fix by going to the Auth email templates section in the Firebase Console.",
    "auth/invalid-multi-factor-session":
      "The request does not contain a valid proof of first factor successful sign-in.",
    "auth/invalid-oauth-provider":
      "EmailAuthProvider is not supported for this operation. This operation only supports OAuth providers.",
    "auth/invalid-oauth-client-id":
      "The OAuth client ID provided is either invalid or does not match the specified API key.",
    "auth/unauthorized-domain":
      "This domain is not authorized for OAuth operations for your Firebase project. Edit the list of authorized domains from the Firebase console.",
    "auth/invalid-action-code":
      "The action code is invalid. This can happen if the code is malformed, expired, or has already been used.",
    "auth/wrong-password":
      "The password is invalid or the user does not have a password.",
    "auth/invalid-persistence-type":
      "The specified persistence type is invalid. It can only be local, session or none.",
    "auth/invalid-phone-number":
      "The format of the phone number provided is incorrect. Please enter the phone number in a format that can be parsed into E.164 format. E.164 phone numbers are written in the format [+][country code][subscriber number including area code].",
    "auth/invalid-provider-id": "The specified provider ID is invalid.",
    "auth/invalid-recipient-email":
      "The email corresponding to this action failed to send as the provided recipient email address is invalid.",
    "auth/invalid-sender":
      "The email template corresponding to this action contains an invalid sender email or name. Please fix by going to the Auth email templates section in the Firebase Console.",
    "auth/invalid-verification-id":
      "The verification ID used to create the phone auth credential is invalid.",
    "auth/invalid-tenant-id": "The Auth instance's tenant ID is invalid.",
    "auth/multi-factor-info-not-found":
      "The user does not have a second factor matching the identifier provided.",
    "auth/multi-factor-auth-required":
      "Proof of ownership of a second factor is required to complete sign-in.",
    "auth/missing-android-pkg-name":
      "An Android Package Name must be provided if the Android App is required to be installed.",
    "auth/auth-domain-config-required":
      "Be sure to include authDomain when calling firebase.initializeApp(), by following the instructions in the Firebase console.",
    "auth/missing-app-credential":
      "The phone verification request is missing an application verifier assertion. A reCAPTCHA response token needs to be provided.",
    "auth/missing-verification-code":
      "The phone auth credential was created with an empty SMS verification code.",
    "auth/missing-continue-uri":
      "A continue URL must be provided in the request.",
    "auth/missing-iframe-start": "An internal error has occurred.",
    "auth/missing-ios-bundle-id":
      "An iOS Bundle ID must be provided if an App Store ID is provided.",
    "auth/missing-multi-factor-info":
      "No second factor identifier is provided.",
    "auth/missing-multi-factor-session":
      "The request is missing proof of first factor successful sign-in.",
    "auth/missing-or-invalid-nonce":
      "The request does not contain a valid nonce. This can occur if the SHA-256 hash of the provided raw nonce does not match the hashed nonce in the ID token payload.",
    "auth/missing-phone-number":
      "To send verification codes, provide a phone number for the recipient.",
    "auth/missing-verification-id":
      "The phone auth credential was created with an empty verification ID.",
    "auth/app-deleted": "This instance of FirebaseApp has been deleted.",
    "auth/account-exists-with-different-credential":
      "An account already exists with the same email address but different sign-in credentials. Sign in using a provider associated with this email address.",
    "auth/network-request-failed":
      "A network error (such as timeout, interrupted connection or unreachable host) has occurred.",
    "auth/no-auth-event": "An internal error has occurred.",
    "auth/no-such-provider":
      "User was not linked to an account with the given provider.",
    "auth/null-user":
      "A null user object was provided as the argument for an operation which requires a non-null user object.",
    "auth/operation-not-allowed":
      "The given sign-in provider is disabled for this Firebase project. Enable it in the Firebase console, under the sign-in method tab of the Auth section.",
    "auth/operation-not-supported-in-this-environment":
      "This operation is not supported in the environment this application is running on. 'location.protocol' must be http, https or chrome-extension and web storage must be enabled.",
    "auth/popup-blocked":
      "Unable to establish a connection with the popup. It may have been blocked by the browser.",
    "auth/popup-closed-by-user":
      "The popup has been closed by the user before finalizing the operation.",
    "auth/provider-already-linked":
      "User can only be linked to one identity for the given provider.",
    "auth/quota-exceeded":
      "The project's quota for this operation has been exceeded.",
    "auth/redirect-cancelled-by-user":
      "The redirect operation has been cancelled by the user before finalizing.",
    "auth/redirect-operation-pending":
      "A redirect sign-in operation is already pending.",
    "auth/rejected-credential":
      "The request contains malformed or mismatching credentials.",
    "auth/second-factor-already-in-use":
      "The second factor is already enrolled on this account.",
    "auth/maximum-second-factor-count-exceeded":
      "The maximum allowed number of second factors on a user has been exceeded.",
    "auth/tenant-id-mismatch":
      "The provided tenant ID does not match the Auth instance's tenant ID",
    "auth/timeout": "The operation has timed out.",
    "auth/user-token-expired":
      "The user's credential is no longer valid. The user must sign in again.",
    "auth/too-many-requests":
      "We have blocked all requests from this device due to unusual activity. Try again later.",
    "auth/unauthorized-continue-uri":
      "The domain of the continue URL is not whitelisted.  Please whitelist the domain in the Firebase console.",
    "auth/unsupported-first-factor":
      "Enrolling a second factor or signing in with a multi-factor account requires sign-in with a supported first factor.",
    "auth/unsupported-persistence-type":
      "The current environment does not support the specified persistence type.",
    "auth/unsupported-tenant-operation":
      "This operation is not supported in a multi-tenant context.",
    "auth/unverified-email": "The operation requires a verified email.",
    "auth/user-cancelled":
      "The user did not grant your application the permissions it requested.",
    "auth/user-not-found":
      "There is no user record corresponding to this identifier. The user may have been deleted.",
    "auth/user-disabled":
      "The user account has been disabled by an administrator.",
    "auth/user-mismatch":
      "The supplied credentials do not correspond to the previously signed in user.",
    "auth/user-signed-out": "",
    "auth/weak-password": "The password must be 6 characters long or more.",
    "auth/web-storage-unsupported":
      "This browser is not supported or 3rd party cookies and data may be disabled.",
  }

  if (authErrors[error_code] !== undefined) {
    return authErrors[error_code]
  }

  return "An unknown error has occurred. Try again later."
}

export const getTimeDifference = (dateTime) => {
  var date = dateTime.toDate().getTime()
  var now = new Date().getTime()
  let diff = now - date
  var msec = diff

  var years = Math.floor(msec / 1000 / 60 / (60 * 24 * 365))
  var days = Math.floor(msec / 1000 / 60 / (60 * 24))
  var hh = Math.floor(msec / 1000 / 60 / 60)
  msec -= hh * 1000 * 60 * 60
  var mm = Math.floor(msec / 1000 / 60)
  msec -= mm * 1000 * 60
  var ss = Math.floor(msec / 1000)
  msec -= ss * 1000

  if (years > 0) {
    return { value: years, suffix: "years" }
  } else if (days > 0) {
    return { value: days, suffix: "days" }
  } else if (hh > 0) {
    return { value: hh, suffix: "hours" }
  } else {
    return { value: mm, suffix: "minutes" }
  }
}

export const getAge = (user) => {
  if (!user.birthday) return null

  const birthday = user.birthday
  let birth
  if (typeof birthday.toDate === "function") {
    birth = birthday.toDate()
  } else if (typeof birthday === "object" && birthday.seconds) {
    birth = new Date(birthday.seconds * 1000)
  } else {
    birth = new Date(birthday)
  }

  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return { value: age, suffix: "years" }
}

export const getMonthName = (date) => {
  const month = date.toLocaleString("default", { month: "long" })
  return month
}

export const getProperDate = (date) => {
  let month = date.month
  let day = date.day
  let year = date.year
  let actualDate = `${month}/${day}/${year}`
  return actualDate
}

export const getTime = (date) => {
  const today = new Date()
  const time = date.toLocaleTimeString().replace(/(.*)\D\d+/, "$1")

  //check if today - if so return time - if not return date and time
  if (
    date.getDate() == today.getDate() &&
    date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear()
  ) {
    return time
  } else {
    let month = getMonthName(date)
    let day = date.getDate()
    return `${month} ${day} @ ${time}`
  }
}

export const getHeight = (inches) => {
  if (!inches) return '';
  var feet = Math.floor(inches / 12)
  inches %= 12
  return `${feet}' ${inches}"`
}

export const getHobby = (values, noCommas) => {
  let hobbies = []
  values &&
    values.length &&
    values.forEach((x) => {
      let hobby = hobbyOptions.find((v) => v.id === x)?.name
      if (hobbies.length > 0 && !noCommas) {
        hobby = ", " + hobby
      }
      hobbies.push(hobby)
    })
  return hobbies
}

export const getEthnicity = (values, noCommas) => {
  let ethnicities = []

  values &&
    values.length &&
    values.forEach((x) => {
      let ethnicity = ethnicityOptions.find((v) => v.id === x)?.name
      if (ethnicities.length > 0 && !noCommas && ethnicity) {
        ethnicity = ", " + ethnicity
      }
      ethnicity && ethnicities.push(ethnicity)
    })
  return ethnicities
}

export const getReligions = (values, noCommas) => {
  let religions = []
  values &&
    values.length &&
    values.forEach((x) => {
      let rel = religionOptions.find((v) => v.id === x)?.name
      if (religions.length > 0 && !noCommas) {
        rel = ", " + rel
      }
      religions.push(rel)
    })
  return religions
}

export const getProfession = (values) => {
  let selectedProfessions = []
  values &&
    values.length &&
    values.forEach((x) => {
      let profession = professions.find((v) => v.id === x)?.name
      if (selectedProfessions.length > 0) {
        profession = ", " + profession
      }
      selectedProfessions.push(profession)
    })
  return selectedProfessions
}

export const getLanguage = (values) => {
  let languages = []
  values &&
    values.length &&
    values.forEach((x) => {
      let language = languageOptions.find((v) => v.id === x)?.name
      languages.push(language)
    })
  return languages
}

export const getUserInfo = (type, value) => {
  if (value !== "No preference selected") {
    switch (type) {
      case "firstName":
        return value
      case "birthday":
        return value.toDate().toDateString()
      case "gender":
        return genders.find((x) => x.id === value)?.name
      case "homeTown":
        return value
      case "currentCity":
        return value
      case "height":
        return getHeight(value)
      case "vaccinated":
        return value
      case "profession":
        return professions.find((x) => x.id === value)?.name
      case "specialty":
        return specialties.find((x) => x.id === value)?.name
      case "training":
        return trainingLevels.find((x) => x.id === value)?.name
      case "whatAreYouLookingFor":
        return value
      case "radius":
        return value > 100 ? "> 100 mi." : value + " mi."
      case "hobbies":
        return getHobby(value)
      case "lazySunday":
        return value
      case "ethnicity":
        return getEthnicity(value?.length ? value : [value])
      case "religion":
        return religionOptions.find((x) => x.id === value)?.name
      case "family":
        return familyOptions.find((x) => x.id === value)?.name
      case "languages":
        return getLanguage(value)
      case "politicalAffiliation":
        return politicalOptions.find((x) => x.id === value)?.name
      case "bio":
        return value
      case "currentCity":
        return value

      //match preferences
      case "matchPreferenceGender":
        return genderPreferences.find((x) => x.id === value)?.name
      case "matchPreferenceAge":
        return value?.low ? value?.low + " - " + value.high : value
      case "matchPreferenceHeight":
        if (value?.low) {
          if (value.low === minHeight && value.high === maxHeight) {
            return "any"
          } else {
            let low = getHeight(value.low)
            let high = getHeight(value.high)
            if (value.low === minHeight) {
              low = "any"
            }
            if (value.high === maxHeight) {
              high = "any"
            }
            return `${low} - ${high}`
          }
        } else {
          return value
        }
      case "matchPreferenceEthnicity":
        return getEthnicity(value?.length ? value : [value])
      case "matchPreferencePoliticalAffiliation":
        return politicalOptions.find((x) => x.id === value)?.name
      case "matchPreferenceFamily":
        return familyOptions.find((x) => x.id === value)?.name
      case "matchPreferenceReligion":
        return getReligions(value.length ? value : [value])
      case "matchPreferenceProfession":
        return getProfession(value)
      default:
        return value
    }
  } else {
    return value
  }
}

export const getUsersName = (user) => {
  return user?.firstName + " " + user?.lastName
}

export const checkIfChatExistsAndCreate = (
  uid,
  user,
  chats,
  setSelectedChat,
  createChat,
  onSuccess
) => {
  if (chats) {
    let exists = false
    for (let chat of chats) {
      exists = chat.users.length === 2 && !!chat.users.find((x) => x == user.id)
      if (exists) {
        !onSuccess ? setSelectedChat(chat) : onSuccess(chat)
        break
      }
    }

    if (!exists) createChat([user.id, uid], user.resX)
  } else {
    createChat([user.id, uid], user.resX)
  }
}

export const checkIfChatExists = (user, chats, onSuccess) => {
  if (chats) {
    let exists = false
    for (let chat of chats) {
      // console.log('here', chat)
      exists = chat.users.length === 2 && !!chat.users.find((x) => x == user.id)
      if (exists) {
        if (onSuccess) {
          onSuccess(chat)
        } else {
          return chat
        }
      }
    }

    return null
  } else {
    return null
  }
}
